import { sanitizeClockIso } from "./clock";
import { hydrateSlipJournal } from "./slipJournal";
import { parseDivertIcao, shortDivertReason } from "./diversion";
import { safeTrim } from "./text";
import { hydrateTicketAtdSource, parseAtdIso } from "./ticketAtd";
import { hydrateTicketStatusAuthority } from "./ticketComplete";
import { hydrateAcarsLog, hydrateCompanionAcars } from "./acars";
import { hydrateReleasePack } from "./releasePack";
import { hydrateDelayRecords, hydrateFuelUplifts } from "./tripDelayFuel";
import { wholeFuelLb } from "./wholeFuel";
import { hydrateTripSupport } from "./tripSupport";
import { normalizeDeclineReasonId } from "./opsDeskSettings";
import type { FlightType, Mission, MissionStatus } from "./types";

const FLIGHT_TYPES: ReadonlySet<string> = new Set(["golf", "sun", "vip", "corporate", "medical", "ferry"]);
const STATUSES: ReadonlySet<string> = new Set(["pending", "in-progress", "completed", "cancelled"]);
const SOURCES: ReadonlySet<string> = new Set(["ai", "manual", "inbox"]);

function asFiniteNumber(value: unknown, fallback: number): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

function optionalTrimmed(value: unknown): string | undefined {
  if (value == null) return undefined;
  const trimmed = safeTrim(value);
  return trimmed || undefined;
}

function flightTypeOf(value: unknown): FlightType {
  return typeof value === "string" && FLIGHT_TYPES.has(value) ? (value as FlightType) : "corporate";
}

function statusOf(value: unknown): MissionStatus {
  return typeof value === "string" && STATUSES.has(value) ? (value as MissionStatus) : "pending";
}

function sourceOf(value: unknown): Mission["source"] {
  return typeof value === "string" && SOURCES.has(value) ? (value as Mission["source"]) : "inbox";
}

/**
 * Coerce a Nanode / persist / snapshot ticket so board paint never sees
 * undefined on required strings (callsign, ICAO, weatherNote, …).
 * Garbage STD is stripped. Non-objects are dropped.
 */
export function hydrateMission(raw: unknown, index = 0): Mission | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const m = raw as Partial<Mission>;
  delete m.baseRev;
  delete m.reopen;
  const flightNumber = safeTrim(m.flightNumber) || undefined;
  const callsign = safeTrim(m.callsign) || flightNumber || "";
  return {
    ...m,
    id: safeTrim(m.id) || `ns-hydrate-${index}`,
    createdAt: sanitizeClockIso(m.createdAt) || safeTrim(m.createdAt),
    callsign,
    flightNumber,
    flightSeq: typeof m.flightSeq === "number" && Number.isFinite(m.flightSeq) ? m.flightSeq : m.flightSeq,
    aircraftId: safeTrim(m.aircraftId),
    aircraftName: safeTrim(m.aircraftName),
    registration: safeTrim(m.registration),
    flightType: flightTypeOf(m.flightType),
    originIcao: safeTrim(m.originIcao).toUpperCase(),
    destIcao: safeTrim(m.destIcao).toUpperCase(),
    divertToIcao: parseDivertIcao(m.divertToIcao) || undefined,
    divertReason: shortDivertReason(m.divertReason) || undefined,
    originName: safeTrim(m.originName),
    destName: safeTrim(m.destName),
    originCity: safeTrim(m.originCity),
    destCity: safeTrim(m.destCity),
    distanceNm: asFiniteNumber(m.distanceNm, 0),
    eteMin: asFiniteNumber(m.eteMin, 0),
    fuelGal: asFiniteNumber(m.fuelGal, 0),
    fuelLbs: wholeFuelLb(asFiniteNumber(m.fuelLbs, 0)),
    trackTrue: asFiniteNumber(m.trackTrue, 0),
    paxCount: asFiniteNumber(m.paxCount, 0),
    paxNames: Array.isArray(m.paxNames) ? m.paxNames.filter((n): n is string => typeof n === "string") : [],
    scenario: safeTrim(m.scenario),
    purpose: safeTrim(m.purpose),
    departureIso: sanitizeClockIso(m.estimatedStd) || sanitizeClockIso(m.departureIso),
    scheduledStd:
      sanitizeClockIso(m.scheduledStd) || sanitizeClockIso(m.originalStd) || sanitizeClockIso(m.departureIso),
    originalStd:
      sanitizeClockIso(m.originalStd) || sanitizeClockIso(m.scheduledStd) || sanitizeClockIso(m.departureIso),
    estimatedStd: sanitizeClockIso(m.estimatedStd) || sanitizeClockIso(m.departureIso),
    slips: hydrateSlipJournal(m.slips),
    actualDepartureUtc: parseAtdIso(m.actualDepartureUtc) || undefined,
    actualDepartureSource: hydrateTicketAtdSource(m.actualDepartureSource),
    weatherNote: safeTrim(m.weatherNote),
    originMetar: safeTrim(m.originMetar),
    destMetar: safeTrim(m.destMetar),
    status: statusOf(m.status),
    isMaintenance: Boolean(m.isMaintenance),
    serviceLabel: optionalTrimmed(m.serviceLabel),
    originRunwayFt: asFiniteNumber(m.originRunwayFt, 0),
    destRunwayFt: asFiniteNumber(m.destRunwayFt, 0),
    legs: Array.isArray(m.legs)
      ? m.legs.map((leg) => {
          if (!leg || typeof leg !== "object" || !Number.isFinite(leg.fuelLbs)) return leg;
          const fuelLbs = wholeFuelLb(leg.fuelLbs);
          return fuelLbs === leg.fuelLbs ? leg : { ...leg, fuelLbs };
        })
      : [],
    source: sourceOf(m.source),
    seasonNote: optionalTrimmed(m.seasonNote),
    inboxRequestId: optionalTrimmed(m.inboxRequestId),
    dispatchWarning: optionalTrimmed(m.dispatchWarning),
    tripSupport: hydrateTripSupport(m.tripSupport), // on this ticket only — no parallel list
    releasePack: hydrateReleasePack(m.releasePack),
    parentMissionId: optionalTrimmed(m.parentMissionId),
    overnightAtIcao: optionalTrimmed(m.overnightAtIcao),
    cancelReasonNote: optionalTrimmed(m.cancelReasonNote),
    declineReasonId: normalizeDeclineReasonId(m.declineReasonId) || undefined,
    statusAuthority: hydrateTicketStatusAuthority(m.statusAuthority),
    statusAuthorityAt: optionalTrimmed(m.statusAuthorityAt),
    activeBoundAt: optionalTrimmed(m.activeBoundAt),
    boardChange: m.boardChange === "new" || m.boardChange === "ack" || m.boardChange === "changed" ? m.boardChange : undefined,
    rotationHold: m.rotationHold === true ? true : undefined,
    delays: hydrateDelayRecords(m.delays),
    fuelUplifts: hydrateFuelUplifts(m.fuelUplifts),
    acarsLog: hydrateAcarsLog(m.acarsLog),
    companionAcars: hydrateCompanionAcars(m.companionAcars),
    acarsBlockHint: optionalTrimmed(m.acarsBlockHint)?.slice(0, 80),
    slotCtot: typeof m.slotCtot === "string" && /^\d{4}$/.test(m.slotCtot) ? m.slotCtot : undefined,
    releasedDepartureIso: sanitizeClockIso(m.releasedDepartureIso) || undefined,
    releasedPaxCount: typeof m.releasedPaxCount === "number" && Number.isFinite(m.releasedPaxCount) ? Math.round(m.releasedPaxCount) : undefined,
    releasedFuelLbs: typeof m.releasedFuelLbs === "number" && Number.isFinite(m.releasedFuelLbs) ? wholeFuelLb(m.releasedFuelLbs) : undefined,
    techStopSuggestion:
      m.techStopSuggestion && typeof m.techStopSuggestion === "object"
        ? {
            ...m.techStopSuggestion,
            fuelLb: wholeFuelLb(asFiniteNumber(m.techStopSuggestion.fuelLb, 0)),
            secondFuelLb: wholeFuelLb(asFiniteNumber(m.techStopSuggestion.secondFuelLb, 0)),
          }
        : m.techStopSuggestion,
    wxAdvisory:
      m.wxAdvisory &&
      typeof m.wxAdvisory === "object" &&
      typeof m.wxAdvisory.icao === "string" &&
      typeof m.wxAdvisory.cue === "string" &&
      (m.wxAdvisory.source === "metar" || m.wxAdvisory.source === "taf" || m.wxAdvisory.source === "sigmet")
        ? {
            icao: m.wxAdvisory.icao.trim().toUpperCase().slice(0, 4),
            cue: m.wxAdvisory.cue.replace(/\s+/g, " ").trim().slice(0, 80),
            source: m.wxAdvisory.source,
          }
        : undefined,
  };
}

export function hydrateMissions(raw: unknown): Mission[] {
  if (!Array.isArray(raw)) return [];
  const out: Mission[] = [];
  raw.forEach((row, index) => {
    const mission = hydrateMission(row, index);
    if (mission) out.push(mission);
  });
  return out;
}
